"use client";

import React, { useMemo, useState } from "react";
import { useRouter } from "next/navigation";

import {
  LitCovenantFellowshipType,
  getRelationshipFromPOV,
  getOppositeUserFromPOV,
} from "../../../../../../types/network/fellowshipTypes";
import { mdLiteToPlain } from "../../../../../../utils/text/mdLite";
import { AvatarDisplay, Modal, RichTextViewer } from "../../../../../common";
import { Telescope, Fingerprint, Lock, UsersRound, AtSign, HeartHandshake } from "lucide-react";
import "./SpiritualDetails.css";

interface SpiritualDetailsProps {
  biography: string | null;
  vision: string | null;
  showFellowship?: boolean;
  litcovenant: LitCovenantFellowshipType[];
  ownerUserId: number;
}

type PersonRollup = {
  id: number;
  displayName: string;
  username?: string | null;
  profile_url?: string | null;
  avatar_url?: string | null;
  avatar_cdn_url?: string | null;
  profile_image?: string | null;
  labelColor?: string | null;
  is_verified_identity?: boolean;
  is_townlit_verified?: boolean;
  relations: string[];
};

const RELATION_ORDER = [
  "Husband", "Wife",
  "Father", "Mother", "Son", "Daughter",
  "Pastor", "Mentor", "Disciple", "Mentee",
  "Confidant", "Entrusted",
];

const sortRelations = (rels: string[]) =>
  [...new Set(rels)].sort(
    (a, b) => RELATION_ORDER.indexOf(a) - RELATION_ORDER.indexOf(b)
  );

const makeDisplayName = (u: any) => {
  const nf = [u?.name, u?.family].filter(Boolean).join(" ").trim();
  return nf || u?.username || "User";
};

const SpiritualDetails: React.FC<SpiritualDetailsProps> = ({
  biography,
  vision,
  showFellowship = true,
  litcovenant,
  ownerUserId,
}) => {
  const router = useRouter();

  // ------------------------------------------------------------------------
  const InlineField: React.FC<{
    icon: React.ReactNode;
    value?: string | null;
    label: "Biography" | "Vision";
    onClick?: () => void;
  }> = ({ icon, value, label, onClick }) => {
    const hasValue = !!value && value.trim().length > 0;

    return hasValue ? (
      <button
        type="button"
        className="sd-inline"
        onClick={onClick}
        title={label}
        aria-label={`Open full ${label.toLowerCase()}`}
      >
        {icon}
        <span className="sd-inline-text sd-ellipsis-1">{value}</span>
      </button>
    ) : (
      <div className="sd-inline sd-inline-empty" aria-label={`${label} not set`}>
        {icon}
        <span className="sd-placeholder">
          {label === "Biography"
            ? "No bio yet — a short intro helps others get to know you."
            : "Vision not shared yet — your calling in one sentence goes here."
          }
        </span>
      </div>
    );
  };

  // rollup people ----------------------------------------------------------
  const people: PersonRollup[] = useMemo(() => {
    const map = new Map<number, PersonRollup>();

    // 1) sanitize list: drop null/undefined and edges missing users
    const safeCovenant = (litcovenant || []).filter(
      // keep only non-null and with both endpoints and Accepted status
      (f: any): f is LitCovenantFellowshipType =>
        !!f &&
        !!f.from_user &&
        !!f.to_user &&
        (f.status === "Accepted" || f.status === "accepted")
    );

    for (const f of safeCovenant) {
      // defensive: helpers already assume f is valid, but keep ultra-safe usage
      const rel = getRelationshipFromPOV(f, ownerUserId) || "";
      if (!rel) continue;

      const u = getOppositeUserFromPOV(f, ownerUserId) as any;
      if (!u?.id) continue;

      if (!map.has(u.id)) {
        map.set(u.id, {
          id: u.id,
          displayName: makeDisplayName(u),
          username: u.username,
          profile_url: u.profile_url,
          avatar_url: u.avatar_url,
          profile_image: u.profile_image,
          labelColor: u.label?.color ?? null,
          is_verified_identity: !!u.is_verified_identity,
          is_townlit_verified: !!u.is_townlit_verified,
          relations: [rel],
        });
      } else {
        map.get(u.id)!.relations.push(rel);
      }
    }

    const out = Array.from(map.values()).map((p) => ({
      ...p,
      relations: sortRelations(p.relations),
    }));

    out.sort((a, b) => {
      const aIndex = RELATION_ORDER.indexOf(a.relations[0] || "zzz");
      const bIndex = RELATION_ORDER.indexOf(b.relations[0] || "zzz");
      if (aIndex !== bIndex) return aIndex - bIndex;
      return a.displayName.localeCompare(b.displayName);
    });

    return out;
  }, [litcovenant, ownerUserId]);

  const [openBio, setOpenBio] = useState(false);
  const [openVision, setOpenVision] = useState(false);

  // modal for a selected person --------------------------------------------------------
  const [selected, setSelected] = useState<PersonRollup | null>(null);
  const closePerson = () => setSelected(null);
  const openPerson = (p: PersonRollup) => setSelected(p);

  const gotoProfile = () => {
    if (selected?.profile_url) router.push(selected.profile_url);
  };

  const hasAnyRelations = people.length > 0;

  return (
    <section className="sd-container">

      {/* BIO */}
      <InlineField
        icon={<Fingerprint className="sd-ico" size={18} aria-hidden />}
        value={mdLiteToPlain(biography || "")}    // ← plain, single-line
        label="Biography"
        onClick={() => setOpenBio(true)}
      />

      {/* VISION */}
      <InlineField
        icon={<Telescope className="sd-ico" size={18} aria-hidden />}
        value={mdLiteToPlain(vision || "")}       // ← plain, single-line
        label="Vision"
        onClick={() => setOpenVision(true)}
      />

      <span className="sd-cov-head">
        <UsersRound className="sd-ico" size={18} aria-hidden />
        <span className="sd-cov-title">LITCovenant:</span>
      </span>

      {/* LITCovenant */}
      <div className="sd-covenant">


        {hasAnyRelations ? (
          <ul className="sd-people sd-people-only-avatars" aria-label="Covenant relationships">
            {people.map((p) => {
              return (
                <li key={p.id} className="sd-person">
                  <AvatarDisplay
                    userId={p.id}
                    src={p.avatar_cdn_url || null}
                    placeholderSrc={p.avatar_url || p.profile_image || null}
                    alt={p.displayName}
                    shape="rounded"
                    size="md"
                    withBorder
                    borderColor={p.labelColor || undefined}
                    isVerifiedIdentity={!!p.is_verified_identity}
                    isTownlitVerified={!!p.is_townlit_verified}
                    type="user"
                    onClick={() => openPerson(p)}
                    title={p.displayName}
                  />
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="sd-empty" role="status" aria-live="polite">
            {showFellowship ? (
              <>
                <UsersRound className="sd-empty-ico" size={28} aria-hidden />
                <span className="sd-empty-text">No LITCovenant relationships yet</span>
                <span className="sd-empty-sub">
                  When the member adds Pastor, Mentor, or family, they’ll appear here.
                </span>
              </>
            ) : (
              <>
                <Lock className="sd-empty-ico lock" size={28} aria-hidden />
                <span className="sd-empty-text">LITCovenant is private</span>
                <span className="sd-empty-sub">This member has hidden covenant relationships.</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* BIO modal */}
      <Modal
        isOpen={openBio}
        onClose={() => setOpenBio(false)}
        title="Biography"
        hideFooter
        confirmVariant="primary"
      >
        <div className="sd-modal-body">
          <RichTextViewer value={biography} placeholder="No biography yet." />
        </div>
      </Modal>

      {/* VISION modal */}
      <Modal
        isOpen={openVision}
        onClose={() => setOpenVision(false)}
        title="Vision"
        hideFooter
        confirmVariant="primary"
      >
        <div className="sd-modal-body">
          <RichTextViewer value={vision} placeholder="No vision yet." />
        </div>
      </Modal>

      {/* PERSON modal */}
      <Modal
        isOpen={!!selected}
        onClose={closePerson}
        title={selected?.displayName}
        cancelText="Close"
        confirmText={selected?.profile_url ? "Go to profile" : undefined}
        onConfirm={selected?.profile_url ? gotoProfile : undefined}
        confirmVariant="primary"
      >
        {selected && (
          <div className="sd-person-modal">
            <AvatarDisplay
              userId={selected.id}
              src={selected.avatar_cdn_url || null}
              placeholderSrc={selected.avatar_url || selected.profile_image || null}
              alt={selected.displayName}
              shape="rounded"
              size="lg"
              withBorder
              borderColor={selected.labelColor || undefined}
              isVerifiedIdentity={!!selected.is_verified_identity}
              isTownlitVerified={!!selected.is_townlit_verified}
              type="user"
              className="sd-person-modal-avatar"
            />
            <div className="sd-person-modal-info">

              {selected.username && (
                <div className="sd-person-modal-username" title={`@${selected.username}`}>
                  <AtSign className="pd-ico" size={18} aria-hidden />
                  <span className="sd-username-text">{selected.username}</span>
                </div>
              )}

              {selected.relations?.length > 0 && (
                <div className="sd-rel-block">
                  <div className="sd-rel-label" aria-label={selected.relations.length > 1 ? "Relationships" : "Relationship"}>
                    <HeartHandshake className="pd-ico" size={18} aria-hidden />
                    <span className="sr-only">
                      {selected.relations.length > 1 ? "Relationships" : "Relationship"}
                    </span>
                  </div>

                  <div className="sd-person-modal-rel">
                    {selected.relations.map((r) => (
                      <span key={r} className="sd-badge">{r}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      </Modal>
    </section>
  );
};

export default SpiritualDetails;
